import { ChatHidePayload, ChatReadPayload, ChatSendPayload } from "./chat.js";
import { CalendarEntryDonePayload, CalendarEntryRemovePayload, CalendarEntryUpsertPayload } from "./calendar.js";
import { z } from "zod";
import { IsoDateTime, Uuid } from "./common.js";
import {
  AnalyteCreatePayload,
  AnalyteSetActivePayload,
  AnalyteUpdatePayload,
  AreaReorderPayload,
  AreaUpsertPayload,
  ContainerUpsertPayload,
  DocumentTemplateSetPayload,
  EquipmentUpsertPayload,
  MethodUpsertPayload,
  PackageUpsertPayload,
  PriceListDuplicatePayload,
  PriceListUpdatePayload,
  PriceSetPayload,
  SampleTypeUpsertPayload,
  StudyReorderAnalytesPayload,
  StudySetActivePayload,
  StudyUpsertPayload,
  TechniqueUpsertPayload,
} from "./catalogue.js";
import {
  CashCloseCreatePayload,
  CashMovementRecordPayload,
  CashMovementVoidPayload,
  DoctorUpsertPayload,
  OrderAddItemsPayload,
  OrderCancelPayload,
  OrderCreatePayload,
  OrderItemCancelPayload,
  OrderUpdateHeaderPayload,
  PaymentRecordPayload,
  PaymentVoidPayload,
  PaymentCorrectMethodPayload,
  QuoteConvertPayload,
  QuoteCreatePayload,
} from "./commerce.js";
import { BranchPriceListSetPayload, BranchReportSignerSetPayload, SignatureSetPayload, TenantSettingsSetPayload, UserCreatePayload, UserUpdatePayload } from "./identity.js";
import { InstructionImageCreatePayload, InstructionImageUpdatePayload, QuoteWhatsAppSentPayload } from "./instructions.js";
import { AntibioticPanelSetPayload } from "./culture.js";
import { AttachmentUploadPayload } from "./attachments.js";
import { LetterheadRemovePayload, LetterheadSetPayload } from "./letterhead.js";
import { PatientMergePayload, PatientRegisterPayload, PatientUpdatePayload } from "./patients.js";
import {
  AmendPayload,
  DeliveryRecordPayload,
  LabelsPrintedPayload,
  RenderReportPayload,
  ResultsCapturePayload,
  ReturnForRecapturePayload,
  SampleEventPayload,
  ValidatePayload,
} from "./work.js";

/**
 * Every mutation is `POST /v1/commands` (04-delivery-plan.md, S1 conventions). The command id
 * is client-generated (UUIDv7) and doubles as the idempotency key: a replay returns the stored
 * receipt with `outcome: "duplicate"` instead of applying twice.
 *
 * `tenant_id` and `actor_user_id` are optional in the body because the session is the truth;
 * when present they must match the session or the command is rejected.
 */
export const CommandPayloads = {
  "patient.register": PatientRegisterPayload,
  "patient.update": PatientUpdatePayload,
  "patient.merge": PatientMergePayload,

  "doctor.upsert": DoctorUpsertPayload,

  "quote.create": QuoteCreatePayload,
  "quote.convert": QuoteConvertPayload,

  "order.create": OrderCreatePayload,
  "order.add_items": OrderAddItemsPayload,
  "order.cancel": OrderCancelPayload,
  "order.update_header": OrderUpdateHeaderPayload,
  "order_item.cancel": OrderItemCancelPayload,

  "payment.record": PaymentRecordPayload,
  "payment.void": PaymentVoidPayload,
  "payment.correct_method": PaymentCorrectMethodPayload,
  "cash_close.create": CashCloseCreatePayload,
  /** Extra income / expense in the drawer (migration 0030); voided with a reason, never deleted. */
  "cash_movement.record": CashMovementRecordPayload,
  "cash_movement.void": CashMovementVoidPayload,

  "sample.event": SampleEventPayload,
  "labels.printed": LabelsPrintedPayload,

  "results.capture": ResultsCapturePayload,
  "results.validate": ValidatePayload,
  "results.return": ReturnForRecapturePayload,
  "results.amend": AmendPayload,
  /** A document for a study (PDF or photo), staged until a capture or amendment names it (ADR 0030). */
  "results.attachment.upload": AttachmentUploadPayload,

  "report.render": RenderReportPayload,
  "delivery.record": DeliveryRecordPayload,

  "catalogue.area.upsert": AreaUpsertPayload,
  /** The report order of the areas: every active area, once. */
  "catalogue.area.reorder": AreaReorderPayload,
  "catalogue.sample_type.upsert": SampleTypeUpsertPayload,
  "catalogue.container.upsert": ContainerUpsertPayload,
  "catalogue.method.upsert": MethodUpsertPayload,
  "catalogue.technique.upsert": TechniqueUpsertPayload,
  "catalogue.equipment.upsert": EquipmentUpsertPayload,
  "catalogue.study.upsert": StudyUpsertPayload,
  /** The print order of one study's analytes: every active analyte, once, subtitles included. */
  "catalogue.study.reorder_analytes": StudyReorderAnalytesPayload,
  /** «Quitar del catálogo» / «Volver a activar»: never deletes, refuses a study inside an active package unless forced. */
  "catalogue.study.set_active": StudySetActivePayload,
  /** Catálogo → Analitos: one analyte, and explicitly the same clave in other studies; each study a new version. */
  "catalogue.analyte.update": AnalyteUpdatePayload,
  /** «Nuevo analito»: a new clave inside one or more studies, at the end of each list. */
  "catalogue.analyte.create": AnalyteCreatePayload,
  /** «Eliminar del catálogo» / «Volver a activar» on an analyte: never deletes, new version in each study. */
  "catalogue.analyte.set_active": AnalyteSetActivePayload,
  "catalogue.package.upsert": PackageUpsertPayload,
  "catalogue.price.set": PriceSetPayload,
  "catalogue.price_list.duplicate": PriceListDuplicatePayload,
  "catalogue.price_list.update": PriceListUpdatePayload,
  /** One of the two antibiotic panels of culture antibiograms, whole, in print order (ADR 0026). */
  "catalogue.antibiotic_panel.set": AntibioticPanelSetPayload,
  /** The report template a document analyte's editor starts with ("" = none). */
  "catalogue.document_template.set": DocumentTemplateSetPayload,

  "user.create": UserCreatePayload,
  "user.update": UserUpdatePayload,

  /** Who signs the branch's reports: each validator, or the responsable sanitario (NOM-007 5.1.8). */
  "branch.report_signer.set": BranchReportSignerSetPayload,

  /** Which price list new orders and quotes use at the branch (migration 0018); null = default list. */
  "branch.price_list.set": BranchPriceListSetPayload,

  /** The acting user's own signature image; never another user's (no user id in the payload). */
  "signature.set": SignatureSetPayload,

  /** The laboratory's interface settings (Administrador, migration 0014); audited in the chain. */
  "tenant.settings.set": TenantSettingsSetPayload,

  /** The lab's patient instruction images (Administrador, migration 0019). */
  "instruction_image.create": InstructionImageCreatePayload,
  "instruction_image.update": InstructionImageUpdatePayload,
  /** The counter sent a quote by WhatsApp: chained with the quote and image ids, never the phone. */
  "quote.whatsapp_sent": QuoteWhatsAppSentPayload,

  /** The laboratory's letterhead and the report's free zone (Administrador, migration 0023). */
  "letterhead.set": LetterheadSetPayload,
  "letterhead.remove": LetterheadRemovePayload,

  /** Calendario entries (ADR 0027): any staff role; never support (not an operator command). */
  "calendar.entry.upsert": CalendarEntryUpsertPayload,
  "calendar.entry.remove": CalendarEntryRemovePayload,
  "calendar.entry.done": CalendarEntryDonePayload,

  /** Chat entre sucursales (ADR 0028): any staff role; never support. Hide = the author only. */
  "chat.message.send": ChatSendPayload,
  "chat.message.hide": ChatHidePayload,
  "chat.read": ChatReadPayload,
} as const;

export type CommandType = keyof typeof CommandPayloads;
export const CommandType = z.enum(Object.keys(CommandPayloads) as [CommandType, ...CommandType[]]);

export type CommandPayload<T extends CommandType> = z.infer<(typeof CommandPayloads)[T]>;

/** The envelope as received. `payload` is validated against `CommandPayloads[type]` by the API. */
export const CommandEnvelope = z.object({
  id: Uuid,
  type: CommandType,
  tenant_id: Uuid.optional(),
  branch_id: Uuid.optional(),
  actor_user_id: Uuid.optional(),
  /** Device id; the enrolled-device model is deferred, so any stable client id is accepted. */
  device_id: z.string().trim().min(1).max(120).default("unknown"),
  /** Device clock at the moment of the action. */
  occurred_at: IsoDateTime,
  payload: z.unknown(),
});
export type CommandEnvelope = z.infer<typeof CommandEnvelope>;

export const CommandOutcome = z.enum(["applied", "duplicate", "rejected"]);
export type CommandOutcome = z.infer<typeof CommandOutcome>;

export const CommandReceipt = z.object({
  id: Uuid,
  type: CommandType,
  outcome: CommandOutcome,
  /** Handler result (e.g. the created entity) when applied, or the original result on duplicate. */
  result: z.unknown().optional(),
  error: z.object({ type: z.string(), title: z.string(), detail: z.string().optional() }).optional(),
  received_at: IsoDateTime,
});
export type CommandReceipt = z.infer<typeof CommandReceipt>;

/** Typed helper for clients: build a validated envelope for a known command type. */
export function buildCommand<T extends CommandType>(
  type: T,
  payload: CommandPayload<T>,
  opts: { id: string; occurred_at?: string; branch_id?: string; device_id?: string },
): CommandEnvelope {
  return {
    id: opts.id,
    type,
    payload,
    occurred_at: opts.occurred_at ?? new Date().toISOString(),
    device_id: opts.device_id ?? "unknown",
    ...(opts.branch_id ? { branch_id: opts.branch_id } : {}),
  };
}
